import React from 'react';
import { Box, Typography } from '@mui/material';

import {
  SectionContent,
  PageHeader,
  StyledList,
  InfoCard,
  VideoPlayer,
  FlowDiagram,
  ComparisonTable,
  TwoColumnLayout,
  CardCarousel,
  ContactLink
} from '../../components';
import SEO from '../../components/SEO';
import { STRATEGIES_CONTENT } from '../../data';
import { seoData } from '../../data/seoData';
import ROUTES from '../../config/routes';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
const strandQImage =
  'https://www.dropbox.com/scl/fi/jj19yudkp3cc7067hpwtp/program-strand-q-cover.png?rlkey=paq5nmwsw258r7bmf1jpb406t&st=xvldo25a&raw=1';
const merinImage =
  'https://www.dropbox.com/scl/fi/mov1sbupfv5kx30sinvrj/program-merin-cover.png?rlkey=gdqhaaotomo65jyki00z1rsqv&st=dvljd4wc&raw=1';
const dravonImage =
  'https://www.dropbox.com/scl/fi/0qw1ukrv4xn0ycrsrou0p/program-dravon-cover.png?rlkey=p7r5qu17vhn5hh405yj4m7x1j&st=15nlkcy4&raw=1';
const globalMacroImage =
  'https://www.dropbox.com/scl/fi/ax4t63m2jtxy1wocgvwtl/program-global-macro-cover.jpg?rlkey=m7x82le8yg2cndg892jtqovc2&st=onm7ui5u&raw=1';
const graphVideo =
  'https://www.dropbox.com/scl/fi/6pl6gv7ijuyt7a9znk6iv/graph-video-02.mov?rlkey=ll4jnysaz6jdi63zr649886ug&st=v3he5kl3&raw=1';

/**
 * StrategiesPage component for displaying information about the company's strategies and programs
 * Uses ID-based navigation instead of refs for better performance and cleaner code
 * @returns {React.Element} StrategiesPage component
 */
const StrategiesPage = () => {
  const sections = STRATEGIES_CONTENT.SECTIONS;

  return (
    <>
      <SEO
        title={seoData.strategies.title}
        content={seoData.strategies.content}
        keywords={seoData.strategies.keywords}
        canonical={seoData.strategies.canonical}
        ogImage={seoData.strategies.ogImage}
        ogType={seoData.strategies.ogType}
      />
      <Box sx={{ backgroundColor: THEME.COLORS.NEUTRAL_WHITE }}>
        {/* Page Header */}
        <PageHeader
          id='hero'
          title={STRATEGIES_CONTENT.TITLE}
          subtitle={STRATEGIES_CONTENT.SUBTITLE}
          path={ROUTES.STRATEGIES.ROOT}
        />

        {/* Video with overlay text for the Technology section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <VideoPlayer
            videoSrc={graphVideo}
            videoType='direct'
            autoPlay={true}
            muted={true}
            controls={false}
            loop={true}
            useCustomContainer={true}
            fullWidth={true}
            height='400px'
            overlayOpacity={0}
            overlay={{
              title: '',
              text: sections.TECHNOLOGY.TEXT[2],
              textColor: THEME.COLORS.NEUTRAL_BLACK,
              position: { vertical: 'center', horizontal: 'center' },
              textSx: {
                fontSize: '1.4rem',
                lineHeight: 1.6,
                padding: { xs: '0', sm: '0' },
                textAlign: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: '4px',
                margin: { xs: '0 4px', sm: '0 15%', md: '0 25%' },
                maxWidth: { xs: '98%', sm: '90%', md: '70%' },
                boxSizing: 'border-box',
                wordBreak: 'break-word',
                display: 'block',
                width: 'fit-content',
                textShadow: 'none'
              }
            }}
          />
        </Box>

        {/* Technology Section */}
        <SectionContent
          id='technology'
          title={sections.TECHNOLOGY.TITLE}
          titleVariant='TitleMedium'
          titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
          initialSpacing={true}
          linkMappings={sections.TECHNOLOGY.LINKS}
          text={[
            {
              text: sections.TECHNOLOGY.TEXT[0],
              marginBottom: ELEMENT_SPACING,
              fontSize: '1.3rem'
            },
            {
              text: sections.TECHNOLOGY.TEXT[1],
              marginBottom: ELEMENT_SPACING,
              fontSize: '1.3rem'
            }
          ]}
        />

        {/* Programs Section */}
        <SectionContent
          id='programs'
          title={sections.PROGRAMS.TITLE}
          titleVariant='TitleMedium'
          titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
          initialSpacing={true}
          text={[]}
        />

        {/* Program Cards Carousel */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <CardCarousel
            items={[
              {
                ...sections.PROGRAMS.STRAND_Q,
                imageSrc: strandQImage,
                imageAlt: 'Strand Q'
              },
              {
                ...sections.PROGRAMS.MERIN,
                imageSrc: merinImage,
                imageAlt: 'Merin'
              },
              {
                ...sections.PROGRAMS.DRAVON,
                imageSrc: dravonImage,
                imageAlt: 'Dravon'
              },
              {
                ...sections.PROGRAMS.GLOBAL_MACRO,
                imageSrc: globalMacroImage,
                imageAlt: 'Global Macro'
              }
            ]}
            expectedMaxCardHeightPx={500}
            spaceForDotsPx={30}
            renderCard={(item, index, isActive) => (
              <InfoCard
                key={`program-${index}`}
                variant='elevated'
                title={item.TITLE}
                description={item.LABEL}
                bulletPoints={item.BULLET_POINTS}
                imageSrc={item.imageSrc}
                imageAlt={item.imageAlt}
                imageBadge={
                  item.TITLE === 'StrandQ'
                    ? 'Inception 19/01/2024'
                    : item.TITLE === 'Merin'
                      ? 'Inception 10/11/2025'
                      : item.TITLE === 'Dravon'
                        ? 'Inception 01/09/2025'
                        : 'Incubation'
                }
                linkTo={item.LINK}
                sx={{
                  height: 'auto',
                  opacity: isActive ? 1 : 0.8,
                  transition: 'all 0.3s ease',
                  minWidth: 280,
                  maxWidth: 350
                }}
              />
            )}
          />
        </Box>

        {/* Comparison Table */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <ComparisonTable
            headers={sections.PROGRAMS.COMPARISON_TABLE.HEADERS}
            rows={sections.PROGRAMS.COMPARISON_TABLE.ROWS.map(row => ({
              feature: row.FEATURE,
              values: row.VALUES
            }))}
          />
        </Box>

        {/* SwingVision Section */}
        <SectionContent
          id='swingvision'
          title={sections.SWINGVISION.TITLE}
          titleVariant='TitleMedium'
          titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
          initialSpacing={true}
          text={[
            {
              text: sections.SWINGVISION.TEXT[0],
              fontSize: '1.3rem',
              marginBottom: ELEMENT_SPACING
            }
          ]}
        />

        <Box
          sx={{
            width: '100%',
            paddingLeft: {
              xs: THEME.SPACING.MEDIUM,
              sm: THEME.SPACING.MEDIUM,
              md: THEME.SPACING.XL
            },
            marginBottom: SUBSECTION_SPACING
          }}
        >
          <StyledList
            variant='default'
            items={sections.SWINGVISION.FEATURES.map(feature => ({
              TEXT: feature
            }))}
          />
        </Box>

        {/* SwingVision Flow Diagram */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <FlowDiagram
            steps={sections.SWINGVISION.FLOW_STEPS}
            backgroundColor={THEME.COLORS.PRIMARY_BLUE}
            textColor={THEME.COLORS.NEUTRAL_BLACK}
            arrowColor={THEME.COLORS.NEUTRAL_WHITE}
          />
        </Box>

        {/* Two Column Layout: Accessing details on left, Risk Management on right */}
        <Box sx={{ mt: SUBSECTION_SPACING }}>
          <TwoColumnLayout
            marginTop={0}
            marginBottom={SUBSECTION_SPACING}
            leftContent={
              <Box>
                <SectionContent
                  id='accessing'
                  title={sections.ACCESSING.TITLE}
                  titleVariant='TitleMedium'
                  titleMargin={`0 0 ${ELEMENT_SPACING} 0`}
                  initialSpacing={false}
                  text={[
                    {
                      text: sections.ACCESSING.INTRO,
                      fontSize: '1.3rem',
                      marginBottom: SUBSECTION_SPACING
                    }
                  ]}
                />

                <SectionContent
                  initialSpacing={false}
                  title={sections.ACCESSING.AMC.TITLE}
                  titleVariant='TitleSmall'
                  titleMargin={`0 0 ${ELEMENT_SPACING} 0`}
                  text={[
                    {
                      text: sections.ACCESSING.AMC.TEXT,
                      fontSize: '1.3rem',
                      marginBottom: SUBSECTION_SPACING
                    }
                  ]}
                />

                <SectionContent
                  initialSpacing={false}
                  title={sections.ACCESSING.SMA.TITLE}
                  titleVariant='TitleSmall'
                  titleMargin={`0 0 ${ELEMENT_SPACING} 0`}
                  text={[
                    {
                      text: sections.ACCESSING.SMA.TEXT,
                      fontSize: '1.3rem',
                      marginBottom: SUBSECTION_SPACING
                    }
                  ]}
                />

                <SectionContent
                  initialSpacing={false}
                  text={[
                    {
                      text: sections.ACCESSING.TRANSPARENCY,
                      fontSize: '1.3rem',
                      marginBottom: 0
                    }
                  ]}
                />
              </Box>
            }
            rightContent={
              <Box>
                <SectionContent
                  id='riskManagement'
                  initialSpacing={false}
                  title='Risk Management'
                  titleVariant='TitleMedium'
                  titleMargin={`0 0 ${ELEMENT_SPACING} 0`}
                  text={[]}
                />
                <Box
                  sx={{
                    width: '100%',
                    paddingLeft: {
                      xs: 0,
                      sm: 0,
                      md: 0
                    }
                  }}
                >
                  <StyledList
                    variant='default'
                    items={sections.ACCESSING.RISK_MANAGEMENT.map(item => ({
                      TITLE: item.TITLE,
                      TEXT: item.TEXT
                    }))}
                    titleTag='h4'
                    titleVariant='h4'
                  />
                </Box>
              </Box>
            }
          />
        </Box>

        {/* Flow Diagram - Full Width after columns */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <FlowDiagram steps={sections.PROGRAMS.FLOW_STEPS} />
        </Box>

        {/* Footer note with separator line */}
        <Box
          sx={{
            borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
            paddingTop: ELEMENT_SPACING,
            paddingBottom: ELEMENT_SPACING,
            mt: 4,
            mb: 2
          }}
        >
          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: sections.FOOTER_NOTE,
                fontSize: '1rem',
                fontStyle: 'italic',
                color: THEME.COLORS.NEUTRAL_GRAY
              }
            ]}
          />
          <Box mt={ELEMENT_SPACING}>
            <Typography
              variant='body2'
              sx={{
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                fontSize: '1.2rem',
                color: THEME.COLORS.PRIMARY_BLUE,
                lineHeight: 1.6
              }}
            >
              Talk to an Expert –&nbsp;
              <ContactLink
                style={{
                  color: THEME.COLORS.NEUTRAL_GRAY,
                  textDecoration: 'underline',
                  fontSize: '1rem'
                }}
              >
                Schedule Your meeting
              </ContactLink>
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default StrategiesPage;
